import { salaryRoleUrlPath } from '../../../../../utils/routing/salary-url-path';

/**
 * Links a job-detail salary stat row to its matching /salary/{role} hub page.
 * Duplicated (not imported) from lambda/utils/salaryAggregation.ts's slugifyRolePreservingDiacritics
 * — same convention as CURRENCY_RATES in ../utils/salaryConversion.ts — to avoid pulling lambda
 * code into the client bundle. Keep in sync with that implementation.
 *
 * Preserves diacritics (not the lossy ASCII-only slugifyRole) because `jobTitle` here is whatever
 * this job posting displays in the current page locale — for a fi/sv job detail page that's a
 * Finnish/Swedish title, and only a diacritic-preserving slug has a chance of matching the real
 * fi/sv role-page slug generateSalaryStats.ts registered. No round-trip to the slug map is
 * available client-side, so this is a best-effort guess: if it doesn't match, the linked page
 * falls back to English-guess resolution same as any unmapped slug (see +data.server.ts).
 */
function slugifyRole(title: string): string {
  return title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9À-ɏ]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Row types on the job-detail salary chart that have a matching hub page today (TITLE, TITLE+LOCATION). */
const HUB_LINKED_TYPES = new Set(['title', 'titleLocation']);

export interface BuildSalaryHubLinkParams {
  /** SalaryRange.type from SalaryDistributionChart — only 'title'/'titleLocation' resolve to a hub page. */
  type: string;
  jobTitle?: string | null;
  locale?: string;
}

/** Returns the /salary/{role} hub URL for a stat row, or null if that dimension has no hub page yet. */
export function buildSalaryHubLink({ type, jobTitle, locale = 'en' }: BuildSalaryHubLinkParams): string | null {
  if (!jobTitle || !HUB_LINKED_TYPES.has(type)) return null;
  const slug = slugifyRole(jobTitle);
  if (!slug) return null;
  return salaryRoleUrlPath((locale as 'en' | 'fi' | 'sv') ?? 'en', slug);
}
